/**
 * Home Page Component
 */

import React from 'react';
import { Link } from 'react-router-dom';

const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Divorce Property Division Calculator
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Understand how property would be divided in your divorce based on state laws
          </p>
        </header>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="text-blue-500 text-4xl mb-4">📊</div>
            <h3 className="text-xl font-semibold mb-2">State-Specific Calculations</h3>
            <p className="text-gray-600">
              Get accurate calculations based on the specific laws of your state
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="text-green-500 text-4xl mb-4">🔒</div>
            <h3 className="text-xl font-semibold mb-2">Complete Privacy</h3>
            <p className="text-gray-600">
              Your data is never stored. It's only used to generate your report
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="text-purple-500 text-4xl mb-4">📄</div>
            <h3 className="text-xl font-semibold mb-2">Detailed Reports</h3>
            <p className="text-gray-600">
              Receive comprehensive PDF reports with legal references and calculations
            </p>
          </div>
        </div>

        {/* Privacy Promise */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-12 max-w-3xl mx-auto">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-yellow-400 text-2xl">⚠️</span>
            </div>
            <div className="ml-4">
              <h4 className="text-lg font-semibold text-yellow-800">
                Your Privacy is Our Priority
              </h4>
              <p className="text-yellow-700 mt-2">
                We do <strong>NOT</strong> store any of your personal information. Your data is processed
                only to generate your report and is immediately deleted. Your information is never shared
                with third parties.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Link
            to="/calculator"
            className="inline-block bg-blue-600 text-white font-semibold py-4 px-8 rounded-lg text-lg hover:bg-blue-700 transition duration-300 shadow-lg"
          >
            Start Your Calculation
          </Link>
        </div>

        {/* Supported States */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-6">
            Supported States
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              'New Mexico',
              'Nevada',
              'Arkansas',
              'Florida',
              'Texas',
              'Georgia',
            ].map((state) => (
              <div key={state} className="text-center py-2 px-4 bg-gray-50 rounded">
                {state}
              </div>
            ))}
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="mt-12 text-center text-sm text-gray-600 max-w-3xl mx-auto">
          <p>
            <strong>Legal Disclaimer:</strong> This calculator provides estimates based on general
            legal principles and does not constitute legal advice. Actual court decisions may vary.
            Please consult with a qualified attorney in your state before making any decisions.
          </p>
        </div>

        {/* Legal Links */}
        <div className="mt-8 text-center">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-blue-600">
            <Link to="/terms-of-use" className="hover:text-blue-800 hover:underline">
              Terms of Use
            </Link>
            <Link to="/privacy-policy" className="hover:text-blue-800 hover:underline">
              Privacy Policy
            </Link>
            <Link to="/disclaimer" className="hover:text-blue-800 hover:underline">
              Disclaimer
            </Link>
            <Link to="/state-references" className="hover:text-blue-800 hover:underline">
              State-Specific Legal References
            </Link>
          </div>
        </div>

        {/* Company Information */}
        <div className="mt-8 border-t border-gray-300 pt-8 max-w-4xl mx-auto">
          <div className="text-center text-xs text-gray-500">
            <p className="font-semibold text-sm text-gray-700 mb-2">TAPNOP LTD</p>
            <div className="space-y-1">
              <p>Registered in England and Wales</p>
              <p>Company No. 17119106</p>
              <p className="max-w-md mx-auto leading-relaxed">
                Unit 3, Office A, 1st Floor, 6-7 St Mary At Hill,<br />
                London EC3R 8EE, England
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;